import React, { useEffect, useState } from 'react';
import classes from './timeAndDate.module.css';
import { sixYardAxios } from '../../../sixYardAxios';
import WhiteArrow from "../../../assets/images/double-arrow.png";
import InputField from '../../containers/inputField/inputField';
import PromptMessage from '../../containers/promptMessage/promptMessage';

const TimeAndDate = (props) => {
    const locationsList = props.sites?.map(location => ({
        value: location.id,
        displayValue: location.name,
    })) || [];
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [types, setTypes] = useState(props.sites?.[0]?.fields);
    const [askForReferee, setAskForReferee] = useState(false);
    const [dateFirst, setDateFirst] = useState(true);
    const [selectedStartTimeId, setSelectedStartTimeId] = useState(null);
    const [timeSlots, setTimeSlots] = useState([]);
    const [footballType, setFootballType] = useState(props.sites?.[0]?.fields?.[0]);
    const [siteControls, setSiteControls] = useState({
        site: {
            elementType: "select",
            elementConfig: {
                type: "select",
                placeholder: props.currentLang === 'en' ? "Select field" : "اختر الملعب",
                options: locationsList
            },
            value: locationsList[0]?.value,
            validation: {
                required: true,
            },
            label: props.currentLang === 'en' ? "Field" : "الملعب",
            valid: false,
            touched: false
        }
    });
    const [dateControls, setDateControls] = useState({
        date: {
            withIcon: true,
            elementType: "input",
            elementConfig: {
                type: "calendarDate",
                placeholder: props.currentLang === 'en' ? "Select date" : "اختر التاريخ",
            },
            value: "",
            validation: {
                required: true,
            },
            label: props.currentLang === 'en' ? "Date" : "التاريخ",
            valid: false,
            touched: false
        },
    });
    const [controls, setControls] = useState({
        startingTime: {
            elementType: "select",
            elementConfig: {
                type: "text",
                placeholder: props.currentLang === 'en' ? "Select time" : "اختر الوقت",
                options: []
            },
            value: "",
            validation: {
                required: true,
            },
            label: props.currentLang === 'en' ? "Starting Time" : "وقت البداية",
            valid: false,
            touched: false
        },
        endingTime: {
            elementType: "select",
            elementConfig: {
                type: "text",
                placeholder: props.currentLang === 'en' ? "Select time" : "اختر الوقت",
                options: [],
            },
            value: "",
            validation: {
                required: true,
            },
            label: props.currentLang === 'en' ? "Finishing Time" : "وقت النهاية",
            valid: false,
            touched: false,
            disabled: true
        }
    });

    useEffect(() => {
        const updatedControls = {
            ...siteControls,
            site: {
                ...siteControls.site,
                elementConfig: {
                    ...siteControls.site.elementConfig,
                    options: locationsList || [], // Ensure fallback to empty array
                },
                value: locationsList[0]?.value
            },
        };
        setSiteControls(updatedControls);
        setTypes(props.sites?.[0]?.fields);
        setFootballType(props.sites?.[0]?.fields?.[0]);
    }, [props.sites]);

    useEffect(() => {
        siteControls.site.value > 0 && handleSiteSelection(siteControls.site.value)
    }, [siteControls.site.value]);

    useEffect(() => {
        dateControls.date.value.length > 0 && handleStartAndEndTimes(dateControls.date.value)
    }, [dateControls.date.value]);

    useEffect(() => {
        if (siteControls.site.value > 0 && dateControls.date.value.length > 0) {
            handleStartAndEndTimes(dateControls.date.value);
        }
    }, [siteControls.site.value, footballType]);

    // Update labels and placeholders when language changes
    useEffect(() => {
        setSiteControls({
            site: {
                ...siteControls.site,
                elementConfig: {
                    ...siteControls.site.elementConfig,
                    placeholder: props.currentLang === 'en' ? "Select field" : "اختر الملعب",
                },
                label: props.currentLang === 'en' ? "Field" : "الملعب"
            }
        });

        setDateControls({
            date: {
                ...dateControls.date,
                elementConfig: {
                    ...dateControls.date.elementConfig,
                    placeholder: props.currentLang === 'en' ? "Select date" : "اختر التاريخ",
                },
                label: props.currentLang === 'en' ? "Date" : "التاريخ"
            }
        });

        setControls({
            startingTime: {
                ...controls.startingTime,
                elementConfig: {
                    ...controls.startingTime.elementConfig,
                    placeholder: props.currentLang === 'en' ? "Select time" : "اختر الوقت",
                },
                label: props.currentLang === 'en' ? "Starting Time" : "وقت البداية"
            },
            endingTime: {
                ...controls.endingTime,
                elementConfig: {
                    ...controls.endingTime.elementConfig,
                    placeholder: props.currentLang === 'en' ? "Select time" : "اختر الوقت",
                },
                label: props.currentLang === 'en' ? "Finishing Time" : "وقت النهاية"
            }
        });
    }, [props.currentLang]);

    const handleSiteSelection = (selectedSite) => {
        const site = props.sites.find(
            (site) => site.id == selectedSite
        );
        const updatedOptions = site ? site.fields : [];
        setTypes(updatedOptions);
        setFootballType(updatedOptions[0]);
    };

    const handleStartAndEndTimes = async (selectedDate) => {
        try {
            const res = await sixYardAxios.get(
                `/fields/time-slots?date=${selectedDate[0]}&site_id=${siteControls.site.value}&field_type=${footballType.type}`
            );
            
            let timeSlotData = res.data.data || [];
            
            try {
                // Filter time slots if booking for the current day
                const today = new Date().toISOString().split('T')[0];
                const selectedDateStr = selectedDate[0];
                
                if (selectedDateStr === today) {
                    const currentHour = new Date().getHours();
                    const currentMinute = new Date().getMinutes();
                    
                    // Filter out times in the past
                    timeSlotData = timeSlotData.filter(time => {
                        if (!time || !time.start_time) return false;
                        
                        try {
                            const timeParts = time.start_time.split(' ');
                            if (timeParts.length !== 2) return true; // Keep if format is unexpected
                            
                            const [timeValue, period] = timeParts;
                            const [hoursStr, minutesStr] = timeValue.split(':');
                            
                            if (!hoursStr || !minutesStr) return true;
                            
                            let hours = parseInt(hoursStr, 10);
                            let minutes = parseInt(minutesStr, 10);
                            
                            // Convert to 24-hour format for comparison
                            if (period === 'PM' && hours !== 12) {
                                hours += 12;
                            } else if (period === 'AM' && hours === 12) {
                                hours = 0;
                            }
                            
                            // Return true if time is in the future
                            return (hours > currentHour || (hours === currentHour && minutes > currentMinute));
                        } catch (err) {
                            console.log("Error parsing time:", err);
                            return true; // Keep the slot if we can't parse it
                        }
                    });
                }
            } catch (filterError) {
                console.log("Error filtering time slots:", filterError);
                // Fall back to using all time slots if there's an error in filtering
            }
            
            setTimeSlots(timeSlotData); // Save filtered time slots in state

            // Save current values before updating
            const currentStartTime = controls.startingTime.value;
            const currentEndTime = controls.endingTime.value;

            const updatedControls = {
                ...controls,
                startingTime: {
                    ...controls.startingTime,
                    elementConfig: {
                        ...controls.startingTime.elementConfig,
                        options: timeSlotData.map(time => ({
                            value: time.start_time,
                            displayValue: time.start_time,
                            id: time.id,
                        })),
                    },
                    value: currentStartTime || (timeSlotData.length > 0 ? timeSlotData[0].start_time : ""),
                },
                endingTime: {
                    ...controls.endingTime,
                    elementConfig: {
                        ...controls.endingTime.elementConfig,
                        options: [],
                    },
                    value: currentEndTime,
                },
            };
            
            if (!currentStartTime && timeSlotData.length > 0) {
                const firstStartTime = timeSlotData[0].start_time;
                setSelectedStartTimeId(firstStartTime);
                
                const matchingEndTime = timeSlotData
                    .filter(time => time.start_time === firstStartTime)
                    .flatMap(time => time.end_times.map(end => ({ 
                        value: end.time, 
                        displayValue: end.time 
                    })));
                
                updatedControls.endingTime = {
                    ...updatedControls.endingTime,
                    elementConfig: {
                        ...updatedControls.endingTime.elementConfig,
                        options: matchingEndTime,
                    },
                    value: matchingEndTime.length > 0 ? matchingEndTime[0].value : "",
                };
            }
            
            setControls(updatedControls);
        } catch (error) {
            console.error("Failed to fetch time slots:", error);
            setError("Failed to fetch time slots. Please try again.");
        }
    };

    const formElementsArray = [];
    for (let key in controls) {
        formElementsArray.push({
            id: key,
            config: controls[key]
        });
    }
    const form = formElementsArray.map((formElement, i) => {
        const isDisabled = formElement.id === "endingTime" && !selectedStartTimeId;
        return (<div key={i} className={[classes.FormGroup,
        (formElement.id === "date") && classes.FormGroupCalendar].join(" ")}>
            <InputField
                key={formElement.id}
                elementType={formElement.config.elementType}
                elementConfig={{
                    ...formElement.config.elementConfig,
                }}
                disabled={isDisabled}
                value={formElement.config.value}
                invalid={!formElement.config.valid}
                shouldValidate={formElement.config.validation}
                touched={formElement.config.touched}
                withRemove={false}
                withIcon={formElement.config.withIcon}
                label={formElement.config.label}
                changed={event => inputChangedHandler(event, formElement.id, setControls, controls)}
            />
        </div>)
    })
    const checkValidity = ((value, rules) => {
        let isValid = true;
        if (!rules) {
            return true;
        }
        if (rules.required) {
            value = typeof value === 'string' ? value.trim() : value;
            isValid = value !== "" && isValid;
        }
        return isValid;
    })
    const inputChangedHandler = (event, controlName, controlFunction, state) => {
        const value = (controlName === 'date') ? event : event.target.value;
        const updatedControls = {
            ...state,
            [controlName]: {
                ...state[controlName],
                value,
                valid: checkValidity(value, state[controlName].validation),
                touched: true
            }
        };
        if (controlName === 'startingTime') {
            const selectedOption = controls.startingTime.elementConfig.options.find(option => option.value === value);
            setSelectedStartTimeId(selectedOption?.value); // Assuming options include an `id` field            
            const matchingEndTime = timeSlots
            .filter(time => time.start_time === selectedOption?.value)
            .flatMap(time => time.end_times.map(end => ({ value: end.time, displayValue: end.time })));
            
            // Don't reset end time value when changing start time unless necessary
            const currentEndTimeValue = controls.endingTime.value;
            const endTimeExists = matchingEndTime.some(option => option.value === currentEndTimeValue);
            
            updatedControls.endingTime = {
                ...updatedControls.endingTime,
                elementConfig: {
                    ...updatedControls.endingTime.elementConfig,
                    options: matchingEndTime,
                },
                // Only reset if current end time is not in the new options
                value: endTimeExists ? currentEndTimeValue : (matchingEndTime.length > 0 ? matchingEndTime[0].value : ""),
            };
        }

        controlFunction(updatedControls);
    };

    const formatTime = (timeString) => {
        const [time, modifier] = timeString.split(' '); // Split into time and AM/PM
        let [hours, minutes] = time.split(':').map(Number);

        if (modifier === 'PM' && hours !== 12) {
            hours += 12; // Convert PM hours to 24-hour format
        } else if (modifier === 'AM' && hours === 12) {
            hours = 0; // Handle midnight case
        }

        // Format as H:i (e.g., 09:00, 15:30)
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Validate that end_time is selected
        if (!controls.endingTime.value) {
            setError("Please select a finishing time");
            return;
        }
        
        const formData = {
            site_id: siteControls.site.value,
            field_type: footballType.type,
            booking_date: dateControls.date.value[0],
            start_time: controls.startingTime.value && formatTime(controls.startingTime.value),
            end_time: controls.endingTime.value && formatTime(controls.endingTime.value),
            referee_requested: askForReferee,
        }
        
        // Debug log to check values before submission
        console.log("Submitting form with data:", formData);
        
        try {
            const response = await sixYardAxios.post('/fields/calculate-price', formData);
                props.setPaymentDataWithMore(response.data.data)
                props.setTimeAndDateData(formData);
                localStorage.setItem("TimeAndDateData", JSON.stringify(formData))
                props.setCurrentStep(props.currentStep + 1);
                window.scrollTo({ top: 0, behavior: 'smooth' });
            }
        catch (err) {
            setError(err.response?.data?.message || 'Something went wrong. Please try again.');
        }
    };
    const Date = <div className={[classes.FormGroup, classes.FormGroupCalendar].join(" ")}> <InputField
        elementType={dateControls.date.elementType}
        elementConfig={dateControls.date.elementConfig}
        value={dateControls.date.value}
        invalid={!dateControls.date.valid}
        shouldValidate={dateControls.date.validation}
        touched={dateControls.date.touched}
        withIcon={dateControls.date.withIcon}
        label={dateControls.date.label}
        changed={event => inputChangedHandler(event, "date", setDateControls, dateControls)} /></div>
    return (
        <form className={classes.Container} onSubmit={handleSubmit}>
            {/*<div className={classes.Field}>
                <InputField
                    elementType={siteControls.site.elementType}
                    elementConfig={siteControls.site.elementConfig}
                    value={siteControls.site.value}
                    invalid={!siteControls.site.valid}
                    shouldValidate={siteControls.site.validation}
                    touched={siteControls.site.touched}
                    withRemove={false}
                    withIcon={siteControls.site.withIcon}
                    label={siteControls.site.label}
                    changed={event => inputChangedHandler(event, "site", setSiteControls, siteControls)}
                />
            </div>*/}
            <div className={classes.Field}>
                <label className={classes.Label}>
                    {props.currentLang === 'en' ? 'Football Type' : 'نوع الملعب'} 
                    <span className={classes.Required}>*</span>
                </label>
                <div className={classes.RadioGroup}>
                    {types?.map(field =>
                        <label key={field.id} className={footballType.id === field.id ? classes.LabelChecked : ''}>
                            <input
                                type="radio"
                                name="footballType"
                                id={field.id}
                                value={field.id}
                                checked={footballType.id === field.id}
                                onChange={() => setFootballType(field)}
                            />
                            {props.currentLang === 'en' ? field.type : field.type_ar || field.type}
                        </label>
                    )}
                </div>
            </div>
            <div className={classes.Field}>
                <label className={classes.Label}>
                    {props.currentLang === 'en' ? 'Search By' : 'البحث حسب'}
                </label>
                <div className={classes.ButtonGroup}>
                    <button 
                        type="button" 
                        className={dateFirst ? classes.DateButton : classes.TimeButton}
                        onClick={() => setDateFirst(true)}
                    >
                        {props.currentLang === 'en' ? 'Date' : 'التاريخ'}
                    </button>
                    <button 
                        type="button" 
                        className={dateFirst ? classes.TimeButton : classes.DateButton}
                        onClick={() => setDateFirst(false)} 
                        disabled
                    >
                        {props.currentLang === 'en' ? 'Time' : 'الوقت'}
                    </button>
                </div>
            </div>
            {dateFirst && Date}
            {form}
            {!dateFirst && Date}
            <div className={classes.Field}>
                <label className={classes.Checkbox}>
                    <input
                        type="checkbox"
                        name="askForReferee"
                        onChange={() => setAskForReferee(true)}
                    />
                    {props.currentLang === 'en' ? 'Ask for a referee' : 'طلب حكم'}
                </label>
            </div>
            <button type='submit' className={classes.Next}>
                {props.currentLang === 'en' ? 'NEXT' : 'التالي'} 
                <img
                    src={WhiteArrow}
                    alt=""
                    className={[classes.SubmitIcon, props.currentLang === 'ar' ? classes.FlipArrow : ''].join(" ")}
                />
            </button>
            {error && 
                <PromptMessage 
                    message={error} 
                    status={props.currentLang === 'en' ? 'Oops' : 'عفواً'} 
                    button={props.currentLang === 'en' ? 'CLOSE' : 'إغلاق'} 
                    click={() => setError(false)} 
                />
            }
        </form>
    );
};

export default TimeAndDate;
